import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { loginSlice } from "./account/loginSlice";
import mapSlice from "./map/mapSlice";
import { settingsSlice } from "./settingsSlice";

import { getEventMapSlice } from "./marketplace/seatgeek/getEventMapSlice";
import { getLowestComparablesSlice } from "./marketplace/seatgeek/getLowestComparables";

import { getMandatoryNotificationsSlice } from "./notifications/getMandatoryNotificationsSlice";
import { getMappingCountsSlice } from "./notifications/getMappingCountsSlice";

import { getPOSSettingsSlice } from "./pos/getPOSSettingsSlice";
import { updatePOSSettingsSlice } from "./pos/updatePOSSettingsSlice";

import { listCategoriesSlice } from "./pricing/listCategoriesSlice";
import { listCompaniesSlice } from "./pricing/listCompaniesSlice";
import { listHeadlinersSlice } from "./pricing/listHeadlinersSlice";
import { listVenuesSlice } from "./pricing/listVenuesSlice";
import { queryEventInventoryAllSlice } from "./pricing/queryEventInventoryAllSlice";
import { queryGlobalInventorySlice } from "./pricing/queryGlobalInventorySlice";
import { queryEventsSlice } from "./pricing/queryEventsSlice";
import { queryInventorySlice } from "./pricing/queryInventorySlice";

import { listCustomersSlice } from "./sales/listCustomersSlice";

import { getActiveListingsSlice } from "./marketplace/seatgeek/getActiveListingsSlice";
import { getSoldListingsSlice } from "./marketplace/seatgeek/getSoldListingsSlice";

import { selectionSlice } from "./pricing/selectionSlice";
import { settingsSlice as pricingSettingsSlice } from "./pricing/settingsSlice";
import { listAllTagsSlice } from "./system/listAllTagsSlice";
import { listCommonHelpSlice } from "./system/listCommonHelpSlice";
import { listExportDestinationsSlice } from "./system/listExportDestinationsSlice";
import { listLocationsSlice } from "./system/listLocationsSlice";
import { viewItemHistorySlice } from "./system/ViewHistory";

import autopricerSlice from "@src/store/auto-pricer/autopricerSlice";
import { appendListingNotesSlice } from "./pricing/appendListingNotes";
import { updateListingExternalNotesSlice } from "./pricing/updateListingExternalNotes";
import { disableFlashSaleSlice } from "./pricing/disableFlashSale";
import { getSharedSettings } from "./pricing/getSharedSettings";
import { holdTicketsSlice } from "./pricing/holdTicketsSlice";
import { holdListingsSlice } from "./pricing/holdListings";
import { eventSummarySlice } from "./pricing/getEventSummary";
import { listingsSharingSlice } from "./pricing/listingsSharing";
import { releaseListingsSlice } from "./pricing/releaseListings";
import { savedRulesSlice } from "./pricing/savedRulesSlice";
import { updateSplitListingSlice } from "./pricing/splitListings";
import { updateSplitListingAdvancedSlice } from "./pricing/splitListingsAdvanced";
import { updateFlashSaleSlice } from "./pricing/updateFlashSale";
import { updateListingCostsSlice } from "./pricing/updateListingCosts";
import { updateListingPositionSlice } from "./pricing/updateListingPosition";
import { updateListingPositionBulkSlice } from "./pricing/updateListingPositionBulk";
import { updateListingPricesSlice } from "./pricing/updateListingPrices";
import { updateSchedulePriceSlice } from "./pricing/updateSchedulePrice";
import { getListingsItemsSlice, listingsItemsSlice } from "./purchasing/getListingsItemsSlice";
import { updatePurchaseInfoSlice } from "./purchasing/updatePurchaseInfo";
import { updateListingInHandSlice } from "./purchasing/updateListingInHand";
import { createExternalInvoiceSlice } from "./sales/createExternalInvoice";
import { createExternalInvoiceBulkSlice } from "./sales/createExternalInvoiceBulk";
import { createInvoiceSlice } from "./sales/createInvoice";
import { createPaymentSlice } from "./sales/createPayment";
import { cancelInvoiceSlice } from "./sales/cancelInvoice";
import { createAdjustmentSlice } from "./sales/createAdjustment";
import { deleteAdjustmentSlice } from "./sales/deleteAdjustment";
import { listAdjustmentOrganizationsSlice } from "./sales/listAdjustmentOrganizations";
import { listAdjustmentOrganizationSubcategoriesSlice } from "./sales/listAdjustmentOrganizationSubcategories";
import { listCategoriesSalesSlice } from "./sales/listCategoriesSlice";
import { listExchangesSlice } from "./sales/listExchanges";
import { listHeadlinersSalesSlice } from "./sales/listHeadlinersSlice";
import { listMarketplacesSlice } from "./sales/listMarketplacesSlice";
import { listSaleStatusesSlice } from "./sales/listSalesStatusesSlice";
import { listTagsSlice } from "./sales/listTagsSlice";
import { listVenuesSalesSlice } from "./sales/listVenueSlice";
import { querySalesSlice } from "./sales/querySalesSlice";
import { addEventTagsSlice } from "./system/addEventTagsSlice";
import { addItemTagsSlice } from "./system/addItemsTagsSlice";
import { attachPdfsSlice } from "./system/attachPdfs";
import { editTicketsSlice } from "./system/editTickets";
import { forceAttachPdfsSlice } from "./system/forceAttachPdfs";
import { getEventTagsSlice } from "./system/getEventTags";
import { getFileSlice } from "./system/getFile";
import { getSeatsSlice } from "./system/getGetSeats";
import { getItemPngSlice } from "./system/getItemPng";
import { getListingTagsSlice } from "./system/getListingTags";
import { removeEventTagSlice } from "./system/removeEventTag";
import { removeTagSlice } from "./system/removeTag";
import { splitPDFsSlice } from "./system/splitPDFs";
import { supportAttachmentRequestSlice } from "./system/supportAttachmentRequest";
import { getListingRulesSlice } from "./auto-pricer/getListingRulesSlice";
import { removeAutopricerSlice } from "./auto-pricer/removeAutopricerSlice";
import { getSeasonInventory } from "./auto-pricer/getSeasonInventory";
import { salesSlice } from "@src/store/sales/salesSlice";
import { getAirbillSlice } from "./sales/getAirbillSlice";
import { paymentHistorySlice } from "@src/store/system/getPaymentHistory";
import { getActiveChartsPlusSlice } from "./marketplace/seatgeek/getChartActiveDataPlus";
import { getSoldChartsPlusSlice } from "./marketplace/seatgeek/getChartSoldDataPlus";
import { helpAndSupportSlice } from "./system/helpAndSupportSlice";
import { contactUserSlice } from "./system/contactUserSlice";
import { syncLogs } from "./pricing/syncLogs";
import { getTNctiveListingsSlice } from "./marketplace/ticketsNow/getTNActiveListingsSlice";
import { getTicketNetworkActiveListingsSlice } from "./marketplace/ticketNetwork/getTNActiveListingsSlice";
import changeSeatNumbersSlice from "./pricing/changeSeatNumbersSlice";
import { relistListingsSlice } from "./pricing/relistListings";
import { reviewedListingsSlice } from "./pricing/reviewedListingsSlice";
import { editTicketStockSlice } from "./pricing/editTicketStock";
import { duplicateListingsSlice } from "./pricing/duplicateListings";
import { duplicateListingEventsSlice } from "./pricing/duplicateListingEventsSlice";
import { newPoEventsSlice } from "./pricing/queryNewPoEventsSlice";
import { createPurchaseOrderSlice } from "./purchasing/createPurchaseOrder";
import { listPoCategoriesSlice } from "./purchasing/listPoCategoriesSlice";
import { listPoHeadlinersSlice } from "./purchasing/listPoHeadlinersSlice";
import { listPoVenuesSlice } from "./purchasing/listPoVenuesSlice";
import { getGametimeSalesSlice } from "@src/store/marketplace/gameTime/getSoldListingsGameTime";
import { getGametimeListingsSlice } from "@src/store/marketplace/gameTime/getActiveListingsGameTime";
import { deleteListingSlice } from "./pricing/deleteListing";
import { eventFilterSlice } from "./events/eventFilterSlice";
import { tndSlice } from "./events/tndSlice";
import { eventCategoriesSlice } from "./events/eventCategoriesSlice";
import { eventArtistsSlice } from "./events/eventArtistsSlice";
import { eventStatesSlice } from "./events/eventStatesSlice";
import { eventCitiesSlice } from "./events/eventCitiesSlice";
import { eventVenuesSlice } from "./events/eventVenuesSlice";
import { eventStatusSlice } from "./events/eventStatusSlice";
import { suggestedTagsSlice } from "./system/suggestedTags";
import { credentialStatisticsSlice } from "./passwordManager/getCredentialStatistics";
import { credentialsSlice } from "./passwordManager/getCredentials";
import { companiesSlice } from "./passwordManager/getCompanies";
import { companyAutoPOSlice } from "./passwordManager/getCompanyAutoPO";
import { missingInformationCountSlice } from "./autoPo/getMissingInformationCount";
import { updateNotesReducer } from "./passwordManager/updateNotes";
import { getPasswordReducer } from "./passwordManager/getPassword";
import { updateNotificationEmailReducer } from "./passwordManager/updateNotificationEmail";
import { updateEnableNotificationsSlice } from "./passwordManager/updateEnableNotifications";
import { deleteNotificationEmailReducer } from "./passwordManager/deleteNotificationEmail";
import { updatePasswordReducer } from "./passwordManager/updatePassword";
import { vendorsSlice } from "./passwordManager/getVendors";
import { addNewCredentialReducer } from "./passwordManager/addNewCredential";
import { triggerAccountSweepReducer } from "./passwordManager/triggerAccountSweep";
import { checkUserReducer } from "./passwordManager/checkUser";
import { bulkUploadCredentialsReducer } from "./passwordManager/bulkUploadCredentials";
import { deleteCredentialReducer } from "./passwordManager/deleteCredential";
import { refreshPasswordManagerTokenReducer } from "./passwordManager/refreshPasswordManagerToken";
import { createVendorReducer } from "./passwordManager/createVendor";
import { getNotesReducer } from "./passwordManager/getNotes";
import { vendorParentsSlice } from "./passwordManager/getVendorParents";
import { vendorsFullDataSlice } from "./passwordManager/getVendorsFullData";
import { propagationPreviewSlice } from "./passwordManager/getPropagationPreview";
import { updateVendorReducer } from "./passwordManager/updateVendor";
import { deleteVendorReducer } from "./passwordManager/deleteVendor";
import { companyPersonaReducer } from "./passwordManager/getCompanyPersona";
import { updateCompanyPersonaReducer } from "./passwordManager/updateCompanyPersona";
import { credentialPersonaReducer } from "./passwordManager/getCredentialPersona";
import { updateCredentialPersonaReducer } from "./passwordManager/updateCredentialPersona";
import { resetStatusesReducer } from "./passwordManager/getResetStatuses";
import { triggerPasswordResetReducer } from "./passwordManager/triggerPasswordReset";
import { deleteSchedulePriceSlice } from "./pricing/deleteSchedulePrice";
import { queryUserNotificationsSlice } from "./system/queryUserNotifications";
import { changePasswordSlice } from "./account/changePasswordSlice";
import { forgotPasswordSlice } from "./account/forgotPasswordSlice";
import { getNotificationsSlice } from "./account/getNotificationsSlice";
import { getNoticesSlice } from "./account/getNoticesSlice";
import { changeNotificationsSlice } from "./account/changeNotificationsSlice";
import { getBalanceSlice } from "./creditCard/getBalanceSlice";

const seatgeek = combineReducers({
	eventMap: getEventMapSlice.reducer,
	lowestComparable: getLowestComparablesSlice.reducer,
	activeListings: getActiveListingsSlice.reducer,
	soldListings: getSoldListingsSlice.reducer,
	activeCharts: getActiveChartsPlusSlice.reducer,
	soldCharts: getSoldChartsPlusSlice.reducer,
});

const ticketsNow = combineReducers({
	activeTNListings: getTNctiveListingsSlice.reducer,
});

const ticketNetwork = combineReducers({
	activeTicketNetworkListings: getTicketNetworkActiveListingsSlice.reducer,
});

const gameTime = combineReducers({
	soldListings: getGametimeSalesSlice.reducer,
	activeListings: getGametimeListingsSlice.reducer,
});

const marketplace = combineReducers({
	seatgeek,
	ticketsNow,
	ticketNetwork,
	gameTime,
});

const notification = combineReducers({
	mandatoryNotifications: getMandatoryNotificationsSlice.reducer,
	mappingCounts: getMappingCountsSlice.reducer,
});

const POS = combineReducers({
	POSSettings: getPOSSettingsSlice.reducer,
	updatePOSSettings: updatePOSSettingsSlice.reducer,
});

const pricing = combineReducers({
	categories: listCategoriesSlice.reducer,
	companies: listCompaniesSlice.reducer,
	headliners: listHeadlinersSlice.reducer,
	venues: listVenuesSlice.reducer,
	eventInventoryAll: queryEventInventoryAllSlice.reducer,
	globalInventory: queryGlobalInventorySlice.reducer,
	events: queryEventsSlice.reducer,
	columns: queryEventsSlice.reducer,
	inventory: queryInventorySlice.reducer,
	selection: selectionSlice.reducer,
	settings: pricingSettingsSlice.reducer,
	eventSummary: eventSummarySlice.reducer,
	holdTickets: holdTicketsSlice.reducer,
	holdListings: holdListingsSlice.reducer,
	savedRules: savedRulesSlice.reducer,
	sharingSettings: getSharedSettings.reducer,
	listingsSharing: listingsSharingSlice.reducer,
	listCategories: listCategoriesSlice.reducer,
	listVenues: listVenuesSlice.reducer,
	listHeadliners: listHeadlinersSlice.reducer,
	updateListingPrices: updateListingPricesSlice.reducer,
	updateListingPosition: updateListingPositionSlice.reducer,
	updateListingPositionBulk: updateListingPositionBulkSlice.reducer,
	updateListingCosts: updateListingCostsSlice.reducer,
	schedulePrice: updateSchedulePriceSlice.reducer,
	flashSale: updateFlashSaleSlice.reducer,
	disableFlashSale: disableFlashSaleSlice.reducer,
	splitListings: updateSplitListingSlice.reducer,
	splitListingsAdvanced: updateSplitListingAdvancedSlice.reducer,
	appendListingNotes: appendListingNotesSlice.reducer,
	updateListingExternalNotes: updateListingExternalNotesSlice.reducer,
	releaseListings: releaseListingsSlice.reducer,
	relistListings: relistListingsSlice.reducer,
	reviewedListings: reviewedListingsSlice.reducer,
	editTicketStock: editTicketStockSlice.reducer,
	duplicateListings: duplicateListingsSlice.reducer,
	duplicateListingEvents: duplicateListingEventsSlice.reducer,
	newPoEvents: newPoEventsSlice.reducer,
	syncLogs: syncLogs.reducer,
	changeSeatNumbers: changeSeatNumbersSlice.reducer,
	deleteListing: deleteListingSlice.reducer,
	deleteSchedulePrice: deleteSchedulePriceSlice.reducer,
});

const sales = combineReducers({
	customers: listCustomersSlice.reducer,
	exchanges: listExchangesSlice.reducer,
	createExternalInvoice: createExternalInvoiceSlice.reducer,
	createExternalInvoiceBulk: createExternalInvoiceBulkSlice.reducer,
	createInvoice: createInvoiceSlice.reducer,
	createPayment: createPaymentSlice.reducer,
	cancelInvoice: cancelInvoiceSlice.reducer,
	createAdjustment: createAdjustmentSlice.reducer,
	deleteAdjustment: deleteAdjustmentSlice.reducer,
	listAdjustmentOrganizations: listAdjustmentOrganizationsSlice.reducer,
	listAdjustmentOrganizationSubcategories: listAdjustmentOrganizationSubcategoriesSlice.reducer,
	querySales: querySalesSlice.reducer,
	listVenues: listVenuesSalesSlice.reducer,
	listCategoriesSales: listCategoriesSalesSlice.reducer,
	listHeadliners: listHeadlinersSalesSlice.reducer,
	listSaleStatuses: listSaleStatusesSlice.reducer,
	listMarketplaces: listMarketplacesSlice.reducer,
	listTags: listTagsSlice.reducer,
	salesSlice: salesSlice.reducer,
	getAirbill: getAirbillSlice.reducer,
});

const purchasing = combineReducers({
	listingsItems: getListingsItemsSlice.reducer,
	updatePurchaseOrder: updatePurchaseInfoSlice.reducer,
	updateListingInHand: updateListingInHandSlice.reducer,
	createPurchaseOrder: createPurchaseOrderSlice.reducer,
	poCategories: listPoCategoriesSlice.reducer,
	poHeadliners: listPoHeadlinersSlice.reducer,
	poVenues: listPoVenuesSlice.reducer,
	...listingsItemsSlice.reducer,
});

const system = combineReducers({
	allTags: listAllTagsSlice.reducer,
	suggestedTags: suggestedTagsSlice.reducer,
	commonHelp: listCommonHelpSlice.reducer,
	exportDestinations: listExportDestinationsSlice.reducer,
	locations: listLocationsSlice.reducer,
	viewItemHistory: viewItemHistorySlice.reducer,
	addItemTags: addItemTagsSlice.reducer,
	addEventTags: addEventTagsSlice.reducer,
	editTickets: editTicketsSlice.reducer,
	listingTags: getListingTagsSlice.reducer,
	eventTags: getEventTagsSlice.reducer,
	removeTag: removeTagSlice.reducer,
	removeEventTag: removeEventTagSlice.reducer,
	splitPDFs: splitPDFsSlice.reducer,
	getFile: getFileSlice.reducer,
	attachPdfs: attachPdfsSlice.reducer,
	supportAttachmentRequest: supportAttachmentRequestSlice.reducer,
	forceAttachPdfs: forceAttachPdfsSlice.reducer,
	getSeats: getSeatsSlice.reducer,
	getItemPng: getItemPngSlice.reducer,
	paymentHistory: paymentHistorySlice.reducer,
	helpAndSupport: helpAndSupportSlice.reducer,
	contactUser: contactUserSlice.reducer,
	queryUserNotifications: queryUserNotificationsSlice.reducer,
});

const passwordManager = combineReducers({
	credentialStatistics: credentialStatisticsSlice.reducer,
	credentials: credentialsSlice.reducer,
	companies: companiesSlice.reducer,
	companyAutoPO: companyAutoPOSlice.reducer,
	updateNotes: updateNotesReducer,
	passwords: getPasswordReducer,
	updateNotificationEmail: updateNotificationEmailReducer,
	updateEnableNotifications: updateEnableNotificationsSlice.reducer,
	deleteNotificationEmail: deleteNotificationEmailReducer,
	updatePassword: updatePasswordReducer,
	vendors: vendorsSlice.reducer,
	vendorsFullData: vendorsFullDataSlice.reducer,
	vendorParents: vendorParentsSlice.reducer,
	propagationPreview: propagationPreviewSlice.reducer,
	addNewCredential: addNewCredentialReducer,
	triggerAccountSweep: triggerAccountSweepReducer,
	checkUser: checkUserReducer,
	bulkUploadCredentials: bulkUploadCredentialsReducer,
	deleteCredential: deleteCredentialReducer,
	createVendor: createVendorReducer,
	updateVendor: updateVendorReducer,
	deleteVendor: deleteVendorReducer,
	getNotes: getNotesReducer,
	companyPersona: companyPersonaReducer,
	updateCompanyPersona: updateCompanyPersonaReducer,
	credentialPersona: credentialPersonaReducer,
	updateCredentialPersona: updateCredentialPersonaReducer,
	resetStatuses: resetStatusesReducer,
	triggerPasswordReset: triggerPasswordResetReducer,
	refreshPasswordManagerToken: refreshPasswordManagerTokenReducer,
});

const rootReducer = combineReducers({
	login: loginSlice.reducer,
	changePassword: changePasswordSlice.reducer,
	forgotPassword: forgotPasswordSlice.reducer,
	getNotifications: getNotificationsSlice.reducer,
	getNotices: getNoticesSlice.reducer,
	changeNotifications: changeNotificationsSlice.reducer,
	autopricer: autopricerSlice.reducer,
	autopricerRules: getListingRulesSlice.reducer,
	getSeason: getSeasonInventory.reducer,
	removeAutopricer: removeAutopricerSlice.reducer,
	marketplace,
	notification,
	POS,
	pricing,
	purchasing,
	sales,
	system,
	passwordManager,
	autoPo: combineReducers({
		missingInformationCount: missingInformationCountSlice.reducer,
	}),
	settings: settingsSlice.reducer,
	map: mapSlice.reducer,
	creditCard: combineReducers({
		balance: getBalanceSlice.reducer,
	}),
	events: combineReducers({
		filter: eventFilterSlice.reducer,
		tndId: tndSlice.reducer,
		categories: eventCategoriesSlice.reducer,
		artists: eventArtistsSlice.reducer,
		states: eventStatesSlice.reducer,
		cities: eventCitiesSlice.reducer,
		venues: eventVenuesSlice.reducer,
		statuses: eventStatusSlice.reducer,
	}),
});

export const store = configureStore({
	reducer: rootReducer,
	middleware: (getDefaultMiddleware) =>
		getDefaultMiddleware({
			serializableCheck: false,
		}),
});

export type AppDispatch = typeof store.dispatch;
export type RootState = ReturnType<typeof store.getState>;
